import { useEffect, useMemo, useState } from 'react';

import {
    FEEDING_CONFIG_DEFAULTS,
    type UpdateFeedingDashboardConfig,
} from '@/domain/feedingDashboard';

import type { FeedingConfigPanelProps, FeedingConfigPen } from './FeedingConfigPanel';
import {
    ALL_FIELDS,
    type FieldKey,
    type FlagMap,
    PELLET_FIELDS,
    SITE_ONLY_FIELDS,
    toStrings,
    type ValueMap,
} from './feedingConfigFields';
import { validateConfigValues } from './feedingConfigValidation';

/** 'site' edits feeding_location_config; a number edits that pen's overrides. */
export type Scope = 'site' | number;

export interface FeedingConfigForm {
    open: boolean;
    scope: Scope;
    setScope: (scope: Scope) => void;
    canScopeToPen: boolean;
    activePen: FeedingConfigPen | undefined;
    fieldVisible: (key: FieldKey) => boolean;
    values: ValueMap;
    setValue: (key: FieldKey, value: string) => void;
    openSections: Set<string>;
    toggleSection: (id: string) => void;
    saveError: boolean;
    systemName: string;
    setSystemName: (name: string) => void;
    errors: FlagMap;
    orderErrors: FlagMap;
    isValid: boolean;
    openModal: () => void;
    closeModal: () => void;
    handleSave: () => Promise<void>;
    handleResetPen: () => Promise<void>;
    /** Fills the form with the shipped defaults; still needs saving. */
    resetToDefaults: () => void;
}

/** The config form's state, validation and the save and reset writes. */
export function useFeedingConfigForm(props: FeedingConfigPanelProps): FeedingConfigForm {
    const { config, pens = [], showPelletSettings, saving, onSave, onSavePen } = props;
    const [open, setOpen] = useState(false);
    const [scope, setScope] = useState<Scope>('site');
    // Pens are only offered when the caller can actually persist an override.
    const canScopeToPen = pens.length > 0 && onSavePen != null;
    const activePen = typeof scope === 'number' ? pens.find((p) => p.cageId === scope) : undefined;
    // Pen scope seeds from the pen's RESOLVED config, so the form always shows the
    // number in force, whether inherited or overridden.
    const scopeConfig = activePen?.config ?? config;
    const fieldVisible = (key: FieldKey) =>
        (scope === 'site' || !SITE_ONLY_FIELDS.has(key)) &&
        (showPelletSettings || !PELLET_FIELDS.has(key));
    const [values, setValues] = useState<ValueMap>(() => toStrings(config));
    const [openSections, setOpenSections] = useState<Set<string>>(() => new Set(['fallthrough']));
    const [saveError, setSaveError] = useState(false);
    // Held apart from `values`, which is the numeric-field machinery.
    const [systemName, setSystemName] = useState(() => config.pmsSystemName ?? '');

    // Keep the form in sync with the latest saved config while it is closed; opening
    // re-seeds explicitly so an aborted edit never leaks into the next session.
    useEffect(() => {
        if (!open) setValues(toStrings(config));
    }, [config, open]);

    // Switching scope re-seeds from that scope's values, so a half-typed site edit
    // cannot be saved onto a pen.
    // biome-ignore lint/correctness/useExhaustiveDependencies: seeding is keyed on the
    // scope switch itself; scopeConfig is derived from it and would re-seed on every
    // parent render, discarding the user's typing.
    useEffect(() => {
        setValues(toStrings(scopeConfig));
        setSaveError(false);
    }, [scope]);

    const { parsed, errors, orderErrors, isValid } = useMemo(() => {
        return validateConfigValues(values, fieldVisible);
        // biome-ignore lint/correctness/useExhaustiveDependencies: fieldVisible is a
        // pure function of `scope` and `showPelletSettings`, which are listed.
    }, [values, scope, showPelletSettings]);

    const setValue = (key: FieldKey, value: string) =>
        setValues((prev) => ({ ...prev, [key]: value }));

    const toggleSection = (id: string) =>
        setOpenSections((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });

    // Every session starts on the site: a pen kept from last time would turn the next Save
    // into an override on that pen.
    const openModal = () => {
        setScope('site');
        setValues(toStrings(config));
        setSystemName(config.pmsSystemName ?? '');
        setSaveError(false);
        setOpen(true);
    };

    const closeModal = () => {
        if (saving) return;
        setSaveError(false);
        setOpen(false);
    };

    const handleSave = async () => {
        if (!isValid || saving) return;
        setSaveError(false);
        // Send only the fields the user actually changed, preserving the backend's
        // selective-write (no clobber of a field a concurrent PUT just changed). In pen
        // scope the comparison is against the RESOLVED config, so typing the inherited
        // value back in is correctly treated as no change rather than as a new override.
        const update: UpdateFeedingDashboardConfig = {};
        for (const field of ALL_FIELDS) {
            if (!fieldVisible(field.key)) continue;
            if (parsed[field.key] !== scopeConfig[field.key]) update[field.key] = parsed[field.key];
        }
        // The system label is site-only and not part of the numeric machinery, so it is
        // compared separately. Empty clears it back to the generic wording.
        if (scope === 'site' && (systemName.trim() || null) !== (config.pmsSystemName ?? null)) {
            update.pmsSystemName = systemName.trim() || null;
        }
        // Nothing edited — close without a no-op round-trip (the save would still
        // touch updated_at on the row).
        if (Object.keys(update).length === 0) {
            setOpen(false);
            return;
        }
        try {
            if (activePen && onSavePen) await onSavePen(activePen.cageId, update);
            else await onSave(update);
            setOpen(false);
        } catch {
            setSaveError(true);
        }
    };

    /**
     * Clear every override on this pen. Sent as explicit nulls, which is what the API
     * distinguishes from "absent": absent leaves a stored override alone.
     */
    const handleResetPen = async () => {
        if (!activePen || !onSavePen || saving) return;
        setSaveError(false);
        const cleared: Record<string, null> = {};
        // A pellet override hidden by the flag stays: clearing a value the user cannot
        // see is a change they did not ask for, and it takes effect again once the flag
        // is on.
        for (const field of ALL_FIELDS) {
            if (fieldVisible(field.key)) cleared[field.key] = null;
        }
        try {
            await onSavePen(activePen.cageId, cleared as UpdateFeedingDashboardConfig);
            setOpen(false);
        } catch {
            setSaveError(true);
        }
    };

    const resetToDefaults = () => setValues(toStrings(FEEDING_CONFIG_DEFAULTS));

    return {
        open,
        scope,
        setScope,
        canScopeToPen,
        activePen,
        fieldVisible,
        values,
        setValue,
        openSections,
        toggleSection,
        saveError,
        systemName,
        setSystemName,
        errors,
        orderErrors,
        isValid,
        openModal,
        closeModal,
        handleSave,
        handleResetPen,
        resetToDefaults,
    };
}
